import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import Button from '../Button/Button';

import AddIcon from '../../assets/svgs/AddIcon';
import DownloadIcon from '../../assets/svgs/DownloadIcon'; 
import CheckIcon from '../../assets/svgs/CheckIcon';
import HelpIcon from '../../assets/svgs/HelpIcon';
import HomeIcon from '../../assets/svgs/HomeIcon';
import RegistrationIcon from '../../assets/svgs/RegistrationIcon';
import ExamSummary from '../ExamSummary/ExamSummary';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import getInitialLetters from '../../utils/getInitialLetters';
import sleep from '../../utils/sleep';
import toTitleCase from '../../utils/toTitleCase';
import { useIsMounted } from '../../utils/useIsMounted';
import { setInstitution } from '../../store/actions/settings';

import { MenuStyle } from './MenuStyle';

const Menu = ({ show, style, examSummary, exportExamsToCSV }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const institution = useSelector((state) => state.settings.institution);
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);
  const location = useLocation();

  const [institutions, setInstitutions] = useState([]);
  const [meUser, setMeUser] = useState();
  const [showInstitutionList, setShowInstitutionList] = useState(false);
  const [waitInstitution, setWaitInstitution] = useState(true);

  useEffect(() => {
    (async () => {
      if (!isMounted() || !waitInstitution) {
        return;
      }

      setWaitInstitution(false);

      const response = await Api.getInstitution();

      if (
        waitInstitution &&
        response &&
        response.status &&
        response.status === 200
      ) {
        const currentInstitutions = [];

        if (response?.data?.result?.length) {
          response?.data?.result?.forEach((institutionItem) => {
            if (institutionItem) {
              currentInstitutions.push(institutionItem);
            }
          });

          setInstitutions(currentInstitutions);
        }
      } else {
        await sleep(1000 * 5);
        setWaitInstitution(true);
      }
    })();
  }, [isMounted, language, waitInstitution]);

  useEffect(() => {
    if (!institution?.id && institutions?.length && institutions[0]) {
      dispatch(setInstitution(institutions[0]));
    }
  }, [dispatch, institution, institutions]);

  useEffect(() => {
    (async () => {
      if (!isMounted()) {
        return;
      }

      const response = await Api.getMeUser();

      if (response) {
        // console.log(response)
        setMeUser(response);
      }
    })();
  }, [isMounted]);

  useEffect(() => {
    const redirectAddExam =
      location?.pathname?.includes('adicionar-exame') &&
      institution?.id &&
      meUser &&
      !meUser?.permissions_data?.some(
        (element) =>
          element.permission === 'add_exams' &&
          element.institution === institution?.id
      );

    const redirectInstitution =
      location?.pathname?.includes('instituicoes') &&
      meUser &&
      !meUser?.is_headquarters_admin;

    if (redirectAddExam || redirectInstitution) {
      history.push('/');
    }
  }, [history, institution, location, meUser]);

  const className = useMemo(() => (show ? 'show-menu' : ''), [show]);

  const canAddExam = useMemo(() => {
    if (institution?.id && meUser?.permissions_data?.length) {
      return meUser?.permissions_data.some(
        (element) =>
          element.permission === 'add_exams' &&
          element.institution === institution?.id
      );
    }

    return false;
  }, [institution, meUser]);

  const canRegisterUser = useMemo(
    () => meUser?.type === 'RES' || meUser?.type === 'ADM',
    [meUser?.type]
  );

  const canRegisterInstitution = useMemo(
    () => meUser?.is_headquarters_admin,
    [meUser?.is_headquarters_admin]
  );

  const onClickInstitutionOption = (institutionOption) => {
    if (institutionOption) {
      dispatch(setInstitution(institutionOption));
      setShowInstitutionList(false);
    }
  };

  return (
    <MenuStyle className={className} style={style}>
      <div className="institutions-menu">
        {institution ? (
          <button
            className="selected-institution institution-box"
            type="button"
            onClick={() => setShowInstitutionList(!showInstitutionList)}
          >
            {institution.image ? (
              <div className="institution-avatar">
                <img
                  className="institution-image"
                  src={institution.image}
                  alt={
                    institution.name && institution.name.length
                      ? institution.name
                      : 'avatar'
                  }
                />
              </div>
            ) : (
              <div className="institution-avatar text-avatar">
                {getInitialLetters(institution.name)}
              </div>
            )}
            <div className="institution-information">
              {institution.name && institution.name.length ? (
                <div className="institution-name">{institution.name}</div>
              ) : null}
              {institution.address && institution.address.length ? (
                <div className="institution-adress">{institution.address}</div>
              ) : null}
            </div>
            <span className="institution-check">
              <CheckIcon />
            </span>
          </button>
        ) : null}
        {institutions && institutions.length ? (
          <div
            className={`institution-options-list${
              showInstitutionList ? ' show' : ''
            }`}
          >
            {institutions.map((institutionOptions) => {
              if (
                institutionOptions &&
                institutionOptions.id &&
                institution &&
                institution.id &&
                institutionOptions.id !== institution.id
              ) {
                return (
                  <button
                    key={institutionOptions.id}
                    className="institution-option institution-box"
                    type="button"
                    onClick={() => onClickInstitutionOption(institutionOptions)}
                  >
                    {institutionOptions.image ? (
                      <div className="institution-avatar">
                        <img
                          className="institution-image"
                          src={institutionOptions.image}
                          alt={
                            institutionOptions.name &&
                            institutionOptions.name.length
                              ? institutionOptions.name
                              : 'avatar'
                          }
                        />
                      </div>
                    ) : (
                      <div className="institution-avatar text-avatar">
                        {getInitialLetters(institutionOptions.name)}
                      </div>
                    )}
                    <div className="institution-information">
                      {institutionOptions.name &&
                      institutionOptions.name.length ? (
                        <div className="institution-name">
                          {institutionOptions.name}
                        </div>
                      ) : null}
                      {institutionOptions.address &&
                      institutionOptions.address.length ? (
                        <div className="institution-adress">
                          {institutionOptions.address}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              }
              return null;
            })}
          </div>
        ) : null}
      </div>
      <div className="links-menu">
        {canRegisterInstitution ? (
          <div className="links-menu-item">
            <Button
              type="nav-link"
              theme="light-menu"
              size="small"
              activeTheme="default"
              icon={<HomeIcon />}
              text={toTitleCase(translate('institutions', language))}
              to="/instituicoes"
            />
          </div>
        ) : null}
        <div className="links-menu-item">
          <Button
            type="nav-link"
            theme="light-menu"
            size="small"
            activeTheme="default"
            icon={<HomeIcon />}
            text={translate('examPanel', language)}
            to="/"
          />
        </div>
        {canRegisterUser ? (
          <div className="links-menu-item">
            <Button
              type="nav-link"
              theme="light-menu"
              size="small"
              activeTheme="default"
              icon={<RegistrationIcon />}
              text={translate('registrations', language)}
              to="/painel-de-controle"
            />
          </div>
        ) : null}
        {meUser?.is_headquarters_admin ? (
          <div className="links-menu-item">
            <Button
              type="button"
              theme="light-menu"
              size="small"
              activeTheme="default"
              icon={<DownloadIcon />}
              text={translate('exportData', language)}
              onClick={exportExamsToCSV}
            />
          </div>
        ) : null}
        {canAddExam ? (
          <div className="links-menu-item add-exam-menu-item">
            <Button
              type="nav-link"
              theme="dashed-light"
              activeTheme="default"
              icon={<AddIcon />}
              text={translate('addNewExam', language)}
              to="/adicionar-exame"
            />
          </div>
        ) : null}
{/*         <div className="links-menu-item qr-code-menu-item">
          <Button
            type="external-link"
            theme="dashed-white"
            activeTheme="default"
            text={translate('scanQrCode', language)}
            to="https://healthscan-vr.rocklab.app/"
          />
        </div> */}
        {examSummary ? (
          <div className="exam-summary-wrapper">
            <ExamSummary
              radiographies={examSummary?.radiographies}
              tomographies={examSummary?.tomographies}
            />
          </div>
        )  : null}

        <div className="links-menu-item help-menu-item">
          <Button
            type="nav-link"
            theme="light-menu"
            icon={<HelpIcon />}
            size="small"
            activeTheme="default"
            text={toTitleCase(translate('help', language))}
            to="/ajuda"
          />
        </div>
      </div>
    </MenuStyle>
  );
};

Menu.propTypes = {
  examSummary: PropTypes.shape({
    radiographies: PropTypes.any,
    tomographies: PropTypes.any
  }),
  exportExamsToCSV: PropTypes.func.isRequired,
  show: PropTypes.bool,
  style: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.objectOf(PropTypes.string),
  ]),
};

Menu.defaultProps = {
  show: false,
  style: null,
};

export default Menu;
